// BaseDetailPage — site grid for a single base, the first level of
// the capture drill-down (buildspec 20-ui.md §2, §5.4a).
//
// Data source: PowerSync `useSites` hook (Phase D-2 migration).
// Reads from the local SQLite mirror via @powersync/react's useQuery.
// Results are reactive — any sync-delivered change to `sites` or
// `buildings` tables triggers an automatic re-render. Offline: the
// page renders from cached local data with no error (the offline
// contract validated by the unit tests for `useSites`).
//
// The REST hook (`useCaptureSites`) is retained for reference but is
// no longer called from this page. It remains the source of truth for
// the DTO shape until Phase E finishes the full capture write path.
//
// Ordering: the SQL in `useSites` applies the §5.4a sort
// (display_order ASC, name ASC) so the page iterates in order without
// a client-side sort step.
//
// Breadcrumb: `useSites` also queries the `bases` table for the base
// name, replacing the approach of threading it through the sites
// response envelope.

import { useEffect, useMemo, useState } from 'react'
import { Search as SearchIcon } from 'lucide-react'
import { Link, useParams } from 'react-router'

import { Alert } from '../../ui/alert'
import { Card } from '../../ui/card'
import { EmptyState } from '../../ui/empty-state'
import { Spinner } from '../../ui/spinner'

import { CaptureLayout } from '../components/capture-layout'
import { useSites, type UseSitesRow } from '../../hooks/powersync/use-sites'
import { filterByTokenPrefix } from '../../lib/token-prefix-search'

// 150ms debounce keeps typing responsive while avoiding intermediate
// filter passes. Mirrors the SiteDetailPage pattern.
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)
    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

export function BaseDetailPage() {
  // React Router 7 types route params as `string | undefined`; the
  // route pattern `/capture/bases/:baseId` guarantees non-undefined at
  // runtime, but falling back to an empty string keeps the hook's
  // scope comparison honest without needing a non-null assertion.
  const { baseId = '' } = useParams<{ baseId: string }>()
  const { sites, base, isLoading, error } = useSites(baseId)
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedQuery = useDebouncedValue(searchQuery, 150)

  // Map useQuery's error to a user-safe string matching the previous
  // hook's { error: string | null } contract.
  //
  // Defensive null/undefined check: @powersync/react types `error` as
  // `Error | undefined`, but at runtime returns `null` in some states
  // (observed in dev 2026-05-05 — the previous `!== undefined` check
  // passed for null and crashed on null.message during render).
  // Optional chaining + nullish coalescing handles both shapes.
  const errorMessage = error?.message ?? null

  // PowerSync's useQuery returns `data` as an empty array (never null)
  // while loading, then fills in once the query resolves. Treat an
  // empty sites array combined with isLoading as "still loading".
  const loading = isLoading
  // After loading, sites is the live array (may be empty).
  const loadedSites = isLoading ? null : sites

  const filteredSites = useMemo(() => {
    if (loadedSites === null) return null
    // Token-prefix on name + code so e.g. "out" surfaces "Outstation"
    // but does NOT surface "Hobsonville" (no token starts with "out").
    // Mirrors the global token-prefix rule applied across capture +
    // admin client-side filters.
    return filterByTokenPrefix(loadedSites, debouncedQuery, (site) => [
      site.name,
      site.code,
    ])
  }, [loadedSites, debouncedQuery])

  return (
    <CaptureLayout
      breadcrumb={{
        // Parent of a base page is the capture root (the bases grid).
        // Required by CaptureBreadcrumb after PR #452 Codex review P2.
        parentTo: '/capture',
        base: { label: base?.name ?? 'Base' },
      }}
    >
      <section className="mx-auto w-full max-w-5xl space-y-6">
        <header className="space-y-2">
          <span className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-cc-forest)]/60">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--color-cc-lime)]" />
            Hierarchy
          </span>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-[color:var(--color-cc-forest)]">
            {base?.name ?? 'Base'}
          </h1>
          <h2 className="font-display text-xl font-semibold text-[color:var(--color-cc-forest)]/80">
            Sites
          </h2>
        </header>

        {errorMessage !== null ? (
          <Alert variant="error" title="Could not load sites">
            {errorMessage}
          </Alert>
        ) : loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Spinner label="Loading sites" />
            <span>Loading sites…</span>
          </div>
        ) : loadedSites !== null && loadedSites.length === 0 ? (
          <EmptyState
            title="No sites yet in this base"
            description="Sites will appear once they are added to the location hierarchy."
          />
        ) : loadedSites !== null ? (
          <>
            <label className="flex h-12 items-center gap-2 rounded-2xl border border-[color:var(--color-cc-forest)]/10 bg-white px-3.5 shadow-sm">
              <SearchIcon
                aria-hidden="true"
                className="h-4 w-4 text-[color:var(--color-cc-charcoal)]/60"
              />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search"
                aria-label="Search sites"
                // Suppress the browser's native search-clear glyph so the
                // themed × button is the only clear affordance — without
                // this WebKit/Chromium and Edge-legacy render a second
                // grey X side-by-side with ours. Mirrors the pattern in
                // add-asset-search-results.tsx + site-detail-page.tsx.
                className="min-w-0 flex-1 bg-transparent text-sm text-[color:var(--color-cc-charcoal)] outline-none placeholder:text-[color:var(--color-cc-charcoal)]/45 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-ms-clear]:hidden"
              />
              {searchQuery ? (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                  className="grid h-6 w-6 place-items-center rounded-full bg-[color:var(--color-cc-cream-2)] text-base leading-none text-[color:var(--color-cc-charcoal)]"
                >
                  ×
                </button>
              ) : null}
            </label>
            {filteredSites !== null && filteredSites.length === 0 ? (
              <EmptyState
                title="No sites match your search"
                description={
                  debouncedQuery
                    ? `No sites found for "${debouncedQuery}"`
                    : undefined
                }
              />
            ) : (
              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {(filteredSites ?? loadedSites).map((site) => (
                  <li key={site.id}>
                    <SiteCard site={site} />
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : null}
      </section>
    </CaptureLayout>
  )
}

interface SiteCardProps {
  site: UseSitesRow
}

// Site card is a full-surface tap target (buildspec §9, AGENTS.md
// invariant #10: 44×44 px minimum). Rendered as a <Link> so keyboard
// users get native focus handling and middle-click / open-in-new-tab
// "just work".
function SiteCard({ site }: SiteCardProps) {
  const buildingLabel = `${site.building_count} ${
    site.building_count === 1 ? 'building' : 'buildings'
  }`
  return (
    <Link
      to={`/capture/sites/${site.id}`}
      aria-label={`Open site ${site.name}`}
      className="block rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-cc-lime)]"
    >
      <Card className="min-h-11 p-5 transition-all duration-200 hover:-translate-y-1 hover:border-[color:var(--color-cc-forest)]/40 hover:shadow-lg active:translate-y-0">
        <span className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-cc-forest)]/60">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--color-cc-lime)]" />
          {site.code}
        </span>
        <h3 className="mt-2 truncate font-display text-xl font-semibold text-[color:var(--color-cc-forest)]">
          {site.name}
        </h3>
        <p className="mt-2 text-sm text-[color:var(--color-cc-charcoal)]/70">
          {buildingLabel}
        </p>
      </Card>
    </Link>
  )
}
